<template>
  <div>
    <template v-if="resultsLoading === false">
      <p v-if="regionalCreditScoreGrade">{{ $t('message.credit.alternateCreditVisionInfo') }}</p>
      <h2 v-if="creditScoreGrade" v-html-safe="title"></h2>
      <section v-if="preQualificationLoansExist">
        <p class="text-dark" v-html="$t('message.credit.inventoryMatching.prequalification.title')"></p>
        <finance-lender-card-lite
          :lender-key="getPrequalLenderCategory(preQualificationResult.providerKey)"
          :lender-name="preQualificationResult.providerName"
          :loan-amount="preQualificationResult.amountUpToInDollars"
          :monthly-payment="preQualificationResult.paymentUpToInDollars"
          :loan-term-in-months="preQualificationResult.termUpToInMonths"
          data-test="financeLenderCardLite"
        ></finance-lender-card-lite>
        <CTA
          :label="$t('message.credit.application.preQualification.cim.creditRange')"
          :label-bold="creditRangeRange"
          :ctaStyle="'quaternary'"
          @click="$router.push({ name: `credit-score-details-${baseRoute}` })"
          class="my-3"
          data-test="creditScoreCTA"
          v-av-analytics-cta-interaction="{
            elementText: $t('message.credit.application.preQualification.cim.creditRange'),
            eventActionResult: 'open',
          }"
        ></CTA>
      </section>
      <!-- No prequal loans -->
      <section v-else>
        <CreditScoreDetails :content-only="true" :supplied-credit-score="shownGrade" class="pt-2"></CreditScoreDetails>
      </section>
      <!-- Inventory matches results-->
      <template v-if="hasMatchedInventory">
        <MatchedInventoryResults></MatchedInventoryResults>
      </template>
      <!-- No inventory matches -->
      <template v-else>
        <h3>{{ $t('message.credit.application.preQualification.needMoreInfo.title') }}</h3>
        <ApplyNow class="mt-4" data-test="applyNowCta"></ApplyNow>
        <div class="disclaimer-heading font-size-10"></div>
      </template>
      <!-- disclaimers -->
      <div v-if="preQualificationResult">
        <div
          class="disclaimer-heading font-size-10"
          v-html="
            $t(
              `message.credit.disclaimer.lenderDisclaimer.${getPrequalLenderCategory(
                preQualificationResult.providerKey
              )}`,
              { dealer: getBusinessName }
            )
          "
        ></div>
        <div class="font-size-10 color-primary-grey">{{ getPriceTaxDisclaimerMessage }}</div>
      </div>
      <div class="font-size-10 color-primary-grey pt-3" v-else>
        {{ this.$t('message.credit.disclaimer.equifax') }}
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { PrequalFinanceProviderCodes } from '@/utils/enums';
import FinanceLenderCardLite from '@/components/credit/finance-lender/FinanceLenderCardLite.vue';
import MatchedInventoryResults from '@/components/credit/MatchedInventoryResults.vue';
import helperFunctions from '@/utils/helperFunctions';
import ApplyNow from '@/components/credit/ApplyNow.vue';
import { creditRangesMixin } from '@/mixins';
import CTA from '@/components/CTA.vue';
import CreditScoreDetails from '@/views/credit/CreditScoreDetails.vue';

export default {
  name: 'ConsumerInventoryMatchingResults',
  components: {
    CTA,
    FinanceLenderCardLite,
    MatchedInventoryResults,
    ApplyNow,
    CreditScoreDetails,
  },
  data() {
    return {
      resultsLoading: true,
      prequalFinanceProviderCodes: PrequalFinanceProviderCodes,
    };
  },
  mixins: [creditRangesMixin],
  computed: {
    ...mapGetters('credit', [
      'creditScoreGrade',
      'preQualificationLoansExist',
      'preQualificationResult',
      'regionalCreditScoreGrade',
    ]),
    ...mapGetters('business', ['getIntegrationProvince', 'getBusinessName']),
    ...mapState('credit', ['preQualificationLoans', 'matchedInventoryResults']),
    shownGrade() {
      return this.creditScoreGrade ? this.creditScoreGrade : this.regionalCreditScoreGrade;
    },
    creditRangeRange() {
      return this.getCreditRangeRange(this.shownGrade);
    },
    hasMatchedInventory() {
      return (
        this.matchedInventoryResults &&
        this.matchedInventoryResults.matchedVehicles &&
        this.matchedInventoryResults.matchedVehicles.length > 0
      );
    },
    getPriceTaxDisclaimerMessage() {
      const provinceDisclaimer = helperFunctions.getDisclaimerByProvince(this.getIntegrationProvince);
      return this.$t(provinceDisclaimer, { prefix: '‡ ', postfix: '' });
    },
    title() {
      return !this.preQualificationLoansExist
        ? this.$t('message.credit.application.preQualification.creditReportTitle')
        : this.$t('message.credit.application.preQualification.cim.title');
    },
  },
  methods: {
    getPrequalLenderCategory(lenderKey) {
      switch (lenderKey) {
        case PrequalFinanceProviderCodes.TD:
        case PrequalFinanceProviderCodes.TD_RV:
        case PrequalFinanceProviderCodes.TD_MARINE:
        case PrequalFinanceProviderCodes.TD_POWERSPORTS:
          return PrequalFinanceProviderCodes.TD;
        default:
          return PrequalFinanceProviderCodes.GL;
      }
    },
  },
  async mounted() {
    try {
      if (!this.preQualificationLoansExist) {
        this.$store.commit('SET_LOADING', true);
        await this.$store.dispatch('credit/getPreQualificationLoans');
        await this.$store.dispatch('credit/getMatchedInventory');
      }
    } catch (err) {
      this.$log('Error in beforeCreate credit results page', err);
    } finally {
      this.$store.commit('SET_LOADING', false);
      this.resultsLoading = false;
    }
    try {
      await this.$store.dispatch('lead/updateLead');
    } catch (err) {
      this.$log('Error in update lead credit results page', err);
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables';

.disclaimer-heading {
  color: $primary-dark-text;
}
</style>
